import { RatchetMetricType } from '@dealstack/shared/lib/generated';

export { RatchetMetricType };

export interface SecurityRatchetScheduleRes {
	id: string;
	securityId: string;
	metricType: RatchetMetricType;
	initialEntitlementPercentage: number;
	distributeUntilBreakpoint: boolean;
	entries: Array<{
		id: string;
		scheduleId: string;
		order: number;
		moicThreshold: number | null;
		xirrThreshold: number | null;
		entitlementPercentage: number;
	}>;
	metricSubjects: Array<{
		id: string;
		scheduleId: string;
		personId: string;
	}>;
}

export interface RatchetScheduleItem {
	threshold?: number;
	xirrThreshold?: number;
	entitlement: number;
}

export interface RatchetingData {
	metric: RatchetMetricType;
	subjects: string[];
	schedule: RatchetScheduleItem[];
	initialEntitlement: number;
	distributeUntilBreakpoint: boolean;
}

export type CreateUpdateSecurityRatchetScheduleBody = {
	metricType: RatchetMetricType;
	initialEntitlementPercentage: number;
	distributeUntilBreakpoint: boolean;
	entries: Array<{
		order: number;
		moicThreshold?: number | null;
		xirrThreshold?: number | null;
		entitlementPercentage: number;
	}>;
	metricSubjects: Array<{ personId: string }>;
};

export const mapScheduleResToFormData = (res: SecurityRatchetScheduleRes): RatchetingData => {
	const sortedEntries = [...res.entries].sort((a, b) => a.order - b.order);

	const needsMoic =
		res.metricType === RatchetMetricType.MOIC ||
		res.metricType === RatchetMetricType.MOIC_AND_XIRR;
	const isCombined = res.metricType === RatchetMetricType.MOIC_AND_XIRR;

	return {
		metric: res.metricType,
		subjects: res.metricSubjects.map((s) => s.personId),
		initialEntitlement: res.initialEntitlementPercentage,
		distributeUntilBreakpoint: res.distributeUntilBreakpoint,
		schedule: [
			{ threshold: undefined, entitlement: res.initialEntitlementPercentage },
			...sortedEntries.map((entry) => ({
				threshold: needsMoic
					? entry.moicThreshold ?? undefined
					: entry.xirrThreshold ?? undefined,
				xirrThreshold: isCombined ? entry.xirrThreshold ?? undefined : undefined,
				entitlement: entry.entitlementPercentage,
			})),
		],
	};
};

export const mapFormDataToBody = (
	formData: {
		metric: RatchetMetricType;
		subjects: string[];
		schedule: RatchetScheduleItem[];
		distributeUntilBreakpoint: boolean;
	},
	_initialEntitlement: number
): CreateUpdateSecurityRatchetScheduleBody => {
	const [initialEntry, ...ratchetEntries] = formData.schedule;

	const needsMoic =
		formData.metric === RatchetMetricType.MOIC ||
		formData.metric === RatchetMetricType.MOIC_AND_XIRR;
	const needsXirr =
		formData.metric === RatchetMetricType.XIRR ||
		formData.metric === RatchetMetricType.MOIC_AND_XIRR;
	const xirrInput = (item: RatchetScheduleItem) =>
		formData.metric === RatchetMetricType.MOIC_AND_XIRR ? item.xirrThreshold : item.threshold;

	return {
		metricType: formData.metric,
		initialEntitlementPercentage: initialEntry?.entitlement ?? 0,
		distributeUntilBreakpoint: formData.distributeUntilBreakpoint,
		entries: ratchetEntries.map((item, index) => ({
			order: index + 1,
			moicThreshold: needsMoic ? item.threshold ?? null : null,
			xirrThreshold: needsXirr ? xirrInput(item) ?? null : null,
			entitlementPercentage: item.entitlement,
		})),
		metricSubjects: formData.subjects.map((personId) => ({ personId })),
	};
};
